import type { FC } from 'react';
import { memo, useMemo } from 'react';
import type { AIFormState } from '@trinity/chat';
import type { apiSchemas } from '@trinity/management-app-client';
import type { FormWorkflowIntroConfig } from './iconnect/types';

type FormSchema = apiSchemas.FormSchema;
import { DynamicFormWizard } from './DynamicForm.wizard';
import { IconnectAwareFormWorkflow } from './iconnect/IconnectAwareFormWorkflow';

// Define UI states - same as all the other components
type Step = 'initial' | 'manual-entry' | 'submitted' | 'cancelled';

interface GenericFormWorkflowProps {
  queryId: number;
  queryIdentifier?: string;
  value: Record<string, unknown> & {
    // Core workflow properties
    workflowType?: string; // e.g., 'log-interaction', 'schedule-interaction', etc.
    formState?: Step;
    showButtons?: boolean;
    isFromSpeech?: boolean;
    formConfig?: FormSchema;
    aiState?: AIFormState;
    submittedData?: {
      formData?: Array<{
        fieldLabel: string;
        fieldType: string;
        value: unknown;
      }>;
    };
    submittedAt?: string | number | Date;

    // Workflow-specific UI configuration
    introConfig?: FormWorkflowIntroConfig;
  };
}

// Minimal fallback for truly edge cases
const FALLBACK_CONFIG = {
  warningMessage: '',
  showSpeechOption: true,
  showManualButton: true,
  showCancelButton: true,
};

const GenericFormWorkflowContent: FC<GenericFormWorkflowProps> = memo((props) => {
  const workflowType = props.value.workflowType || 'generic-workflow';
  const finalIntroConfig = useMemo(() => {
    const backendIntroConfig = props.value.introConfig || {};

    return {
      ...backendIntroConfig,
      // Always prefer backend-provided configuration
      prompt: backendIntroConfig.prompt,
      warningMessage: backendIntroConfig.warningMessage || FALLBACK_CONFIG.warningMessage,
      showSpeechOption: backendIntroConfig.showSpeechOption ?? FALLBACK_CONFIG.showSpeechOption,
      showManualButton: backendIntroConfig.showManualButton ?? FALLBACK_CONFIG.showManualButton,
      showCancelButton: backendIntroConfig.showCancelButton ?? FALLBACK_CONFIG.showCancelButton,
      introResponse: backendIntroConfig.introResponse,
    };
  }, [props.value.introConfig]);

  if (finalIntroConfig.iconnectSync?.enabled) {
    return (
      <IconnectAwareFormWorkflow
        queryId={props.queryId}
        queryIdentifier={props.queryIdentifier}
        workflowType={workflowType}
        value={props.value}
        introConfig={finalIntroConfig}
      />
    );
  }

  return (
    <DynamicFormWizard
      queryId={props.queryId}
      queryIdentifier={props.queryIdentifier}
      workflowType={workflowType}
      value={props.value}
      introConfig={finalIntroConfig}
    />
  );
});

/**
 * Main export component with context boundary
 */
export const GenericFormWorkflow: FC<GenericFormWorkflowProps> = (props) => {
  return <GenericFormWorkflowContent {...props} />;
};

export default GenericFormWorkflow;
